import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trans, useTranslation } from "react-i18next";

import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import logo from "@/assets/smart-word-labs-logo.png.asset.json";
import { BRAND_OG_IMAGE, BRAND_OG_IMAGE_ALT, SITE_NAME } from "@/lib/seo";

type Mode = "signin" | "signup";

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.63h6.46a5.52 5.52 0 0 1-2.4 3.62v3h3.88c2.27-2.09 3.58-5.17 3.58-8.8Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.08 7.94-2.93l-3.88-3c-1.08.72-2.45 1.15-4.06 1.15-3.12 0-5.77-2.11-6.71-4.95H1.28v3.1A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.29 14.27a7.2 7.2 0 0 1 0-4.54v-3.1H1.28a12 12 0 0 0 0 10.74l4.01-3.1Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.76 0 3.34.61 4.59 1.8l3.43-3.43C17.95 1.18 15.24 0 12 0A12 12 0 0 0 1.28 6.63l4.01 3.1C6.23 6.86 8.88 4.75 12 4.75Z"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
      <path d="M16.365 1.43c0 1.14-.42 2.2-1.25 3.02-.9.9-1.98 1.42-3.1 1.34a3.4 3.4 0 0 1 1.27-2.98c.83-.86 2.05-1.4 3.08-1.44.02.02.02.04 0 .06ZM20.9 17.06c-.55 1.28-.82 1.85-1.53 2.98-.99 1.58-2.39 3.55-4.12 3.56-1.54.02-1.94-1-4.03-.99-2.09.01-2.52 1.01-4.06.99-1.73-.02-3.05-1.79-4.04-3.37-2.77-4.42-3.06-9.6-1.35-12.36 1.21-1.95 3.13-3.1 4.93-3.1 1.84 0 3 .99 4.5.99 1.47 0 2.36-.99 4.49-.99 1.61 0 3.31.87 4.53 2.38-3.98 2.16-3.34 7.8.68 9.91Z" />
    </svg>
  );
}


export const Route = createFileRoute("/auth")({
  validateSearch: (search: Record<string, unknown>): { mode?: Mode } =>
    search['mode'] === "signup" ? { mode: "signup" } : {},

  head: () => ({
    meta: [
      { title: "Parent Sign In | Smart Word Labs" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content:
          "Sign in to the Smart Word Labs parent portal to manage lessons, billing, progress reports and your children's profiles.",
      },
      { property: "og:title", content: "Parent Sign In | Smart Word Labs" },
      {
        property: "og:description",
        content: "Access your Smart Word Labs parent portal.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:image", content: BRAND_OG_IMAGE },
      { property: "og:image:alt", content: BRAND_OG_IMAGE_ALT },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: BRAND_OG_IMAGE },
      { name: "twitter:image:alt", content: BRAND_OG_IMAGE_ALT },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const { t } = useTranslation("account");
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [mode, setMode] = useState<Mode>(search.mode === "signup" ? "signup" : "signin");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkEmail, setCheckEmail] = useState(false);
  const [magicMode, setMagicMode] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  async function onSocial(provider: "google" | "apple") {
    setLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: window.location.origin,
      });
      if (result.error) {
        toast.error(result.error.message ?? t("auth.errors.couldNotSignIn"));
        return;
      }
      if (result.redirected) return;
      await landAfterAuth();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("auth.errors.couldNotSignIn"));
    } finally {
      setLoading(false);
    }
  }


  // Role always comes from the database (user_roles via has_role), never auth metadata.
  async function landAfterAuth() {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) return;
    const { data: isAdmin } = await supabase.rpc("has_role", {
      _user_id: auth.user.id,
      _role: "admin",
    });
    navigate({ to: isAdmin ? "/admin" : "/dashboard", replace: true });
  }

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) void landAfterAuth();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);


  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin,
            data: {
              role: "parent",
              full_name: fullName,
              country,
              phone_number: phone,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          },
        });
        if (error) throw error;
        if (data.session) {
          navigate({ to: "/dashboard", replace: true });
        } else {
          setCheckEmail(true);
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate({ to: "/dashboard", replace: true });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("auth.errors.somethingWentWrong"));
    } finally {
      setLoading(false);
    }
  }

  async function sendMagicLink(e?: React.FormEvent) {
    e?.preventDefault();
    if (!email) {
      toast.error(t("auth.errors.enterEmailFirst"));
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false, emailRedirectTo: `${window.location.origin}/dashboard` },
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setMagicSent(true);
    toast.success(t("auth.toasts.magicLinkSent"));
  }


  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary/40 px-5 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="flex flex-col items-center gap-3 text-center font-display text-2xl text-primary"
        >
          <img
            src={logo.url}
            alt="Smart Word Labs"
            width={1920}
            height={1047}
            decoding="async"
            className="h-16 w-auto object-contain"
          />
          <span className="sr-only">{t("auth.logoAlt")}</span>
        </Link>

        <div className="surface-card mt-6 p-7">
          {checkEmail ? (
            <div className="text-center">
              <h1 className="text-2xl text-primary">{t("auth.checkEmail.title")}</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                <Trans i18nKey="auth.checkEmail.confirmationBody" t={t} values={{ email }} components={{ strong: <strong /> }} />
              </p>
              <Button
                variant="softOutline"
                className="mt-6"
                onClick={() => {
                  setCheckEmail(false);
                  setMode("signin");
                }}
              >
                {t("auth.checkEmail.backToSignIn")}
              </Button>
            </div>
          ) : magicSent ? (
            <div className="text-center">
              <h1 className="text-2xl text-primary">{t("auth.checkEmail.title")}</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                <Trans i18nKey="auth.checkEmail.magicLinkBody" t={t} values={{ email }} components={{ strong: <strong /> }} />
              </p>
              <Button
                variant="softOutline"
                className="mt-6"
                onClick={() => {
                  setMagicSent(false);
                  setMagicMode(false);
                }}
              >
                {t("auth.checkEmail.backToSignIn")}
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl text-primary">
                {magicMode
                  ? t("auth.heading.magicMode")
                  : mode === "signup"
                    ? t("auth.heading.signup")
                    : t("auth.heading.signin")}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {magicMode
                  ? t("auth.subheading.magicMode")
                  : mode === "signup"
                    ? t("auth.subheading.signup")
                    : t("auth.subheading.signin")}
              </p>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <Button
                  type="button"
                  variant="softOutline"
                  size="lg"
                  className="w-full gap-2"
                  disabled={loading}
                  onClick={() => void onSocial("google")}
                >
                  <GoogleIcon />
                  <span>{t("auth.social.google")}</span>
                </Button>
                <Button
                  type="button"
                  variant="softOutline"
                  size="lg"
                  className="w-full gap-2"
                  disabled={loading}
                  onClick={() => void onSocial("apple")}
                >
                  <AppleIcon />
                  <span>{t("auth.social.apple")}</span>
                </Button>
              </div>

              <div className="my-6 flex items-center gap-3">
                <span className="h-px flex-1 bg-border" />
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t("auth.or")}
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>

              {magicMode ? (
                <form onSubmit={sendMagicLink} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="magic-email">{t("auth.fields.email")}</Label>
                    <Input
                      id="magic-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="hero"
                    size="lg"
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? t("auth.buttons.pleaseWait") : t("auth.buttons.sendMagicLink")}
                  </Button>
                </form>
              ) : (
                <form onSubmit={onSubmit} className="space-y-4">
                  {mode === "signup" && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="fullName">{t("auth.fields.fullName")}</Label>
                        <Input
                          id="fullName"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          maxLength={100}
                          required
                        />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="country">{t("auth.fields.country")}</Label>
                          <Input
                            id="country"
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                            maxLength={80}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone">{t("auth.fields.phone")}</Label>
                          <Input
                            id="phone"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            maxLength={30}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">{t("auth.fields.email")}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">{t("auth.fields.password")}</Label>
                    <Input
                      id="password"
                      type="password"
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="hero"
                    size="lg"
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? t("auth.buttons.pleaseWait") : mode === "signup" ? t("auth.buttons.createAccount") : t("auth.buttons.signIn")}
                  </Button>
                </form>
              )}

              <button
                type="button"
                onClick={() => setMagicMode((v) => !v)}
                className="mt-4 w-full text-center text-sm font-semibold text-accent transition-colors hover:underline"
              >
                {magicMode
                  ? t("auth.toggle.usePassword")
                  : t("auth.toggle.useMagicLink")}
              </button>

              {!magicMode && (
                <p className="mt-5 text-center text-sm text-muted-foreground">
                  {mode === "signup" ? (
                    <>
                      {t("auth.footer.haveAccount")}{" "}
                      <button
                        className="font-semibold text-accent hover:underline"
                        onClick={() => setMode("signin")}
                      >
                        {t("auth.footer.signIn")}
                      </button>
                    </>
                  ) : (
                    <>
                      {t("auth.footer.newHere")}{" "}
                      <Link to="/book" className="font-semibold text-accent hover:underline">
                        {t("auth.footer.bookAssessment")}
                      </Link>
                    </>
                  )}
                </p>
              )}
            </>

          )}
        </div>
      </div>
    </div>
  );
}
